// src/utils/performanceAdapter.ts
import { Performance as PerformanceDB } from '../types/mongodb';
import { Performance as PerformanceCustom } from '../types/Performance';

/**
 * Converts a custom Performance type to the MongoDB model Performance type
 * This is necessary for compatibility with components that expect the MongoDB model structure
 */
export function toDBModel(performance: PerformanceCustom): PerformanceDB {
  if (!performance) return performance as unknown as PerformanceDB;
  
  // Convert dates to strings if needed
  const startTime = typeof performance.startTime === 'string' 
    ? performance.startTime 
    : performance.startTime.toISOString();
    
  const endTime = typeof performance.endTime === 'string' 
    ? performance.endTime 
    : performance.endTime.toISOString();
  
  // Handle accommodation restructuring
  const singleRooms = performance.costs?.accommodation?.singleRooms?.quantity || 0;
  const twinRooms = performance.costs?.accommodation?.twinRooms?.quantity || 0;
  const costPerRoom = performance.costs?.accommodation?.singleRooms?.costPerRoom || 
                      performance.costs?.accommodation?.twinRooms?.costPerRoom || 0;
  const totalNights = performance.costs?.accommodation?.singleRooms?.totalNights || 
                      performance.costs?.accommodation?.twinRooms?.totalNights || 0;
  
  // Create converted performance object
  return {
    ...performance,
    startTime,
    endTime,
    isTicketed: performance.isTicketed ?? true, // Default to true if not specified
    costs: {
      ...performance.costs,
      accommodation: {
        singleRooms,
        twinRooms,
        costPerRoom,
        totalNights
      },
      // Ensure equipment has the right structure
      equipment: {
        rental: performance.costs?.equipment?.rental || 0,
        shipping: performance.costs?.equipment?.shipping || 0
      }
    }
  } as PerformanceDB;
}

/**
 * Converts a MongoDB model Performance type to our custom Performance type
 * This is necessary for working with our calculation functions and nested structure
 */
export function fromDBModel(performance: PerformanceDB): PerformanceCustom {
  if (!performance) return performance as unknown as PerformanceCustom;
  
  const { accommodation } = performance.costs || {};
  
  return {
    ...performance,
    isTicketed: performance.isTicketed ?? true, // Default to true if not specified
    costs: {
      ...performance.costs,
      accommodation: {
        singleRooms: {
          quantity: accommodation?.singleRooms || 0,
          costPerRoom: accommodation?.costPerRoom || 0,
          totalNights: accommodation?.totalNights || 0
        },
        twinRooms: {
          quantity: accommodation?.twinRooms || 0,
          costPerRoom: accommodation?.costPerRoom || 0,
          totalNights: accommodation?.totalNights || 0
        }
      },
      equipment: {
        rental: performance.costs?.equipment?.rental || 0,
        shipping: performance.costs?.equipment?.shipping || 0,
        backline: 0 // Add missing property
      }
    }
  } as PerformanceCustom;
}